import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  Container,
  Card,
  CardBody,
  CardTitle,
  CardText,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Row,
  Col,
} from "reactstrap";

// Import Breadcrumb
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import classnames from "classnames";
import EmployeePersonal from "./personal";
import EmployeeFamily from "./family";
import EmployeeDocuments from "./documents";
import EmployeeOthers from "./others";
import EmployeeAccount from "./account";
import OrganizationalDetails from "./org_details";
import EmployeeEquipment from "./emp_equ";
import WorkExperience from "./work_exp";
import ExitDetails from "./exit_details";
import EducationQualification from "./edu_quali";
import {
  fetchEmployeeDropdown,
  fetchSkills,
  getEmployeeSkills,
  saveEmployeeSkills,
} from "store/actions";
import Swal from 'sweetalert2';
import { useDispatch, useSelector } from "react-redux";
import { useAuthUserDetails } from "components/Common/getAuthUserDetails";
import {urlDecryptData, urlEncryptData} from "../../../helpers/crypto_helper"
import { useLocation,useHistory } from "react-router-dom";
import Loader from "common/pageLoader";
import ItemPicker from "common/ItemPicker/ItemPicker";
import nv5 from "../../../assets/images/brands/b-nav-5.png";

const EmployeeCreation = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [authDetails, setAuthDetails] = useState(null);
  const {
    emp_id,
    dropdownData,
    skillsList,
    loading,
    error,
  } = useSelector((state) => ({
    emp_id: state.EmployeeDetailsReducer?.emp_id,
    dropdownData: state.EmployeeDetailsReducer?.dropdownData,
    skillsList: state.SkillMasterReducer?.skillsList,

    loading: state.EmployeeDetailsReducer?.loading,
    error: state.EmployeeDetailsReducer?.error,
  }));
  // const [filteredData, setFilteredData] = useState(data); 
  const query = new URLSearchParams(useLocation().search);
  const encryptedEmpId = query.get("id");
  const empId = encryptedEmpId ? urlDecryptData(encryptedEmpId) : null;
  const [empID, setEmpID] = useState(empId);
  const [selectedSkills, setSelectedSkills] = useState([]);

  const skillOptions = (skillsList?.all_skills || []).map(skill => ({
    value: String(skill.skill_id),
    label: skill.skill_name,
    description: skill.description,
  }));

  useEffect(() => {
    if (
      skillsList?.all_skills?.length &&
      skillsList?.emp_map_skills?.length
    ) {
      const mappedSkillIds = skillsList.emp_map_skills.map(
        item => String(item.skill_id)
      );

      const preSelected = skillsList.all_skills
        .filter(skill => mappedSkillIds.includes(String(skill.skill_id)))
        .map(skill => ({
          value: String(skill.skill_id),
          label: skill.skill_name,
          description: skill.description,
        }));

      setSelectedSkills(preSelected);
    } else {
      setSelectedSkills([]);
    }
  }, [skillsList]);

  useEffect(() => {
    if (emp_id && emp_id !== "0") {
      // setEmpID(emp_id);
      if (empID !== emp_id) {
        history.replace(`/employee-details?id=${encodeURIComponent(urlEncryptData(emp_id))}`);
      }
    }
  }, [emp_id]);   

  useEffect(() => {
    const authUserDetails = useAuthUserDetails();
    setAuthDetails(authUserDetails[0])
    const unitId = authUserDetails[0].unit_id
    const user_id = authUserDetails[0].user_id
    const formData = new FormData();
    formData.append("emp_id", empID);

    dispatch(fetchEmployeeDropdown(unitId))
    dispatch(getEmployeeSkills(formData))
  }, [dispatch]);

  const [activeMainTab, setActiveMainTab] = useState("Personal Details");
  const [customIconActiveTab, setCustomIconActiveTab] = useState("Personal Information");
  const [orgActiveTab, setOrgActiveTab] = useState("Organizational Information");

  const subTabs = ["Personal Information", "Account Details", "Documents", "Educational Details", "Skills"];
  const orgSubTabs = ["Organizational Information", "Employee Equipment", "Work Experience", "Exit Details"];

  const formikRefs = {
    personal: useRef(),
    family: useRef(),
    documents: useRef()
  };

  const [personalData, setPersonalData] = useState({});
  const [familyData, setFamilyData] = useState({
    fatherFullName: '',
    fatherDOB: null,
    motherFullName: '',
    motherDOB: null,
    guardianFullName: '',
    guardianDOB: null,
    maritalStatus: null,
    spouseName: '',
    spouseDOB: null,
    weddingDate: null,
    rows: []
  });
  const [documentData, setDocumentData] = useState([]);
  const [othersData, setOthersData] = useState({
    educationList: [],
    workExperienceList: [],
    equipmentList: [],
  });

  const toggleIconCustom = (tab) => {
    if (customIconActiveTab !== tab) {
      setCustomIconActiveTab(tab);
    }
  };

  const handleSubmit = async () => {
    let hasError = false;
    let firstErrorTab = null;

    // Iterate over all formik refs (Personal, Family, Others, Documents)
    for (const tab in formikRefs) {
      const formik = formikRefs[tab]?.current;

      if (formik) {
        const formErrors = await formik.validateForm(); // Validate the form fields

        // If there are errors and this is the first error found, switch to this tab
        if (Object.keys(formErrors).length > 0 && !firstErrorTab) {
          firstErrorTab = tab;
        }

        // Ensure Formik updates the UI with errors
        formik.setTouched(
          Object.keys(formErrors).reduce((acc, key) => {
            acc[key] = true;  // Mark all fields as touched
            return acc;
          }, {}),
          false // Prevent revalidation
        );

        hasError = hasError || Object.keys(formErrors).length > 0;
      }
    }

    // If errors exist, switch to the first tab with errors
    if (firstErrorTab) {
      setCustomIconActiveTab(firstErrorTab.charAt(0).toUpperCase() + firstErrorTab.slice(1));
    }

    // If no errors, submit all forms
    if (!hasError) {
      console.log('✅ Form is valid, proceeding with save...');

      // Trigger form submission for each tab
      for (const tab in formikRefs) {
        const formik = formikRefs[tab]?.current;
        if (formik) {
          await formik.submitForm();  // Ensure Formik handles form submission
        }
      }
    }

    const formData = {
      personalData,
      familyData,
      documentData,
      othersData,
    };
    // console.log("Submitted Data:", formData);
  };

  const handleDocumentDataChange = (updatedDocument) => {
    // Update the document list in the parent component when a single document changes
    setDocumentData((prevData) => {
      const index = prevData.findIndex(doc => doc.documentType === updatedDocument.documentType);
      if (index !== -1) {
        // Update the document at the specific index
        prevData[index] = updatedDocument;
      } else {
        // Add new document if not found
        prevData.push(updatedDocument);
      }
      return [...prevData];
    });
  };


  const handleCancel = () => {
    console.log("Cancel button clicked!");
  };

  const handleSubmitSkills = () => {
    const skillsPayload = selectedSkills.map(s => ({
      emp_id: Number(empID),
      skill_id: Number(s.value),
    }));

    const formData = new FormData();

    formData.append(
      "lst_skills_list",
      JSON.stringify({ skills: skillsPayload })
    );

    formData.append("create_by", authDetails?.user_id || "");
    formData.append("unit_id", authDetails?.unit_id || "");

    dispatch(saveEmployeeSkills(formData));
  };

  return (
    <React.Fragment>
      <div className="page-content">
      <Loader loading={loading} />
        <Container fluid>
          {/* Render Breadcrumbs */}
          <Breadcrumbs 
            title="Employee" 
            breadcrumbItem={empID > 0 ? "Update Employee" : "Create Employee"} 
          />


          <Row>
            <Col xs="12">
              <Card>
                <CardBody className="p-0 tabs-style-custom" >
                  {/* Main Horizontal Tabs */}
                  {/* <Nav tabs className="main-nav-line" >
                    {["Personal Details", "Family Details", "Organizational Details"].map((mainTab) => (
                      <NavItem key={mainTab}>
                        <NavLink
                          style={{ cursor: "pointer" }}
                          className={classnames({ active: activeMainTab === mainTab })}
                          onClick={() => setActiveMainTab(mainTab)}
                        >
                          {mainTab}
                        </NavLink>
                      </NavItem>
                    ))}
                  </Nav> */}
                  <Nav tabs className="main-nav-line">
                    {["Personal Details", "Family Details", "Organizational Details"].map((mainTab) => (
                      <NavItem key={mainTab}>
                        <NavLink
                          style={{
                            cursor: empID == 0 && mainTab !== "Personal Details" ? "not-allowed" : "pointer",
                            opacity: empID == 0 && mainTab !== "Personal Details" ? 0.5 : 1,
                          }}
                          className={classnames({ active: activeMainTab === mainTab })}
                          onClick={(e) => {
                            if (empID == 0 && mainTab !== "Personal Details") {
                              e.preventDefault(); // Block interaction
                              return;
                            }
                            setActiveMainTab(mainTab);
                             if (mainTab === "Organizational Details") {
                              setOrgActiveTab("Organizational Information");
                            }
                          }}
                        >
                          {mainTab}
                        </NavLink>
                      </NavItem>
                    ))}
                  </Nav>

                  <div className="panel-body tabs-menu-body main-content-body-right">
                    <TabContent activeTab={activeMainTab} className="">
                      {/* Personal Details Tab with Vertical Sub Tabs */}
                      <TabPane tabId="Personal Details">
                        <div className="d-md-flex">
                          <div className="tab-lft">
                            <div className="panel panel-primary tabs-style-4">
                              <div className="tab-menu-heading">
                                <div className="tabs-menu ">
                                  {/* <Nav tabs className="flex-column panel-tabs">
                                    {subTabs.map((tab, index) => (
                                      <NavItem key={tab} className={classnames({ active: customIconActiveTab === tab },
                                        `nav-ico${index + 1}`, "mb-3")}>
                                        <NavLink
                                          style={{ cursor: "pointer" }}
                                          className={classnames({ active: customIconActiveTab === tab })}
                                          onClick={() => setCustomIconActiveTab(tab)}
                                        >
                                          <span></span>
                                          {tab}
                                        </NavLink>
                                      </NavItem>
                                    ))}
                                  </Nav> */}
                                  <Nav tabs className="flex-column panel-tabs">
                                    {subTabs.map((tab, index) => (
                                      <NavItem
                                        key={tab}
                                        className={classnames(
                                          { active: customIconActiveTab === tab },
                                          `nav-ico${index + 1}`,
                                          "mb-3"
                                        )}
                                      >
                                        <NavLink
                                          style={{ cursor: empID == 0 && tab !== "Personal Information" ? "not-allowed" : "pointer", opacity: empID == 0 && tab !== "Personal Information" ? 0.5 : 1 }}
                                          className={classnames({ active: customIconActiveTab === tab })}
                                          onClick={() => {
                                            if (empID != 0 || tab === "Personal Information") {
                                              setCustomIconActiveTab(tab);
                                            }
                                          }}
                                        >
                                          <span></span>
                                          {tab}
                                        </NavLink>
                                      </NavItem>
                                    ))}
                                  </Nav>

                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Vertical Tab Content */}
                          <div className="tab-rgt tabs-style-4">
                            <div className="panel-body tabs-menu-body">
                              <TabContent activeTab={customIconActiveTab} className="">
                                {subTabs.map((tab) => (
                                  <TabPane key={tab} tabId={tab}>
                                    <Row>
                                      <Col sm="12">
                                        {tab === "Personal Information" && (
                                          <EmployeePersonal empID={empID} dropdownData={dropdownData}  isActive={customIconActiveTab === "Personal Information"} />
                                        )}
                                        {tab === "Account Details" &&
                                          <EmployeeAccount empID={empID} authDetails={authDetails} isActive={customIconActiveTab === "Account Details"}/>
                                        }
                                        {tab === "Documents" && (
                                          <EmployeeDocuments empID={empID} authDetails={authDetails} dropdownData={dropdownData} isActive={customIconActiveTab === "Documents"}/>
                                        )}
                                        {tab === "Educational Details" &&
                                          <EducationQualification empID={empID} authDetails={authDetails} isActive={customIconActiveTab === "Educational Details"}/>
                                        }
                                        {tab === "Skills" && (
                                         <div>
                                           <div className="card-main-title">
                                                <h6 className="title">
                                                    <img src={nv5} className="img-fluid w-auto h-auto" alt="img" />
                                                    Competencies
                                                </h6>
                                            </div>
                                            <ItemPicker
                                                options={skillOptions}
                                                value={selectedSkills}
                                                onChange={setSelectedSkills}
                                                titleLeft="Competencies"
                                                titleRight="My Skills"
                                                leftPlaceholder="Search..."
                                                rightPlaceholder="Search..."
                                                onSubmit={handleSubmitSkills}
                                            />
                                         </div>
                                        )}
                                      </Col>
                                    </Row>
                                  </TabPane>
                                ))}
                              </TabContent>
                            </div>
                          </div>
                        </div>
                      </TabPane>

                      {/* Family Details Tab */}
                      <TabPane tabId="Family Details">
                        <EmployeeFamily empID={empID}  onChange={setFamilyData} formikRef={formikRefs.family} dropdownData={dropdownData} authDetails={authDetails} isActive={activeMainTab === "Family Details"}/>
                      </TabPane>

                      {/* Organizational Details Tab with Vertical Sub Tabs */}
                      <TabPane tabId="Organizational Details">
                        <div className="d-md-flex">
                          <div className="tab-lft">
                            <div className="panel panel-primary tabs-style-4">
                              <div className="tab-menu-heading">
                                <div className="tabs-menu ">
                                  {/* <Nav tabs className="flex-column panel-tabs">
                                    {orgSubTabs.map((tab , index) => (
                                      <NavItem key={tab} className={classnames({ active: customIconActiveTab === tab },
                                        `nav-ico${index + 5}`, "mb-3")}>
                                        <NavLink
                                          style={{ cursor: "pointer" }}
                                          className={classnames({ active: orgActiveTab === tab })}
                                          onClick={() => setOrgActiveTab(tab)}
                                        >
                                          <span></span>
                                          {tab}
                                        </NavLink>
                                      </NavItem>
                                    ))}
                                  </Nav> */}
                                  <Nav tabs className="flex-column panel-tabs">
                                    {orgSubTabs.map((tab, index) => (
                                      <NavItem
                                        key={tab}
                                        className={classnames(
                                          { active: orgActiveTab === tab },
                                          `nav-ico${index + 5}`,
                                          "mb-3"
                                        )}
                                      >
                                        <NavLink
                                          style={{
                                            cursor: empID == 0 ? "not-allowed" : "pointer",
                                            opacity: empID == 0 ? 0.5 : 1,
                                          }}
                                          className={classnames({ active: orgActiveTab === tab })}
                                          onClick={(e) => {
                                            if (empID == 0) {
                                              e.preventDefault(); // Prevent navigation
                                              return;
                                            }
                                            setOrgActiveTab(tab);
                                          }}
                                        >
                                          <span></span>
                                          {tab}
                                        </NavLink>
                                      </NavItem>
                                    ))}
                                  </Nav>
                                </div>
                              </div>
                            </div>
                          </div>


                          {/* Vertical Tab Content */}
                          <div className="tab-rgt tabs-style-4">
                            <div className="panel-body tabs-menu-body">
                              <TabContent activeTab={orgActiveTab} className="">
                                {orgSubTabs.map((tab) => (
                                  <TabPane key={tab} tabId={tab}>
                                    <Row>
                                      <Col sm="12">
                                        {tab === "Organizational Information" && <OrganizationalDetails empID={empID} dropdownData={dropdownData} isActive={orgActiveTab === "Organizational Information"}  triggerKey={activeMainTab}/>}
                                        {tab === "Employee Equipment" &&
                                          <EmployeeEquipment empID={empID} authDetails={authDetails} isActive={orgActiveTab === "Employee Equipment"} dropdownData={dropdownData}/>
                                        }
                                        {tab === "Work Experience" &&
                                          <WorkExperience empID={empID} authDetails={authDetails} dropdownData={dropdownData} isActive={orgActiveTab === "Work Experience"}/>
                                        }
                                        {tab === "Exit Details" &&
                                          <ExitDetails empID={empID} authDetails={authDetails} isActive={orgActiveTab === "Exit Details"}/>
                                        }
                                      </Col>
                                    </Row>
                                  </TabPane>
                                ))}
                              </TabContent>
                            </div>
                          </div>
                        </div>
                      </TabPane>
                    </TabContent>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* <Row>
  <Col xs="12" sm="12" md="12" lg="12">
    <Card>
      <CardBody>
        <div className="d-flex justify-content-between align-items-center mb-3 border-grey flex-wrap">
          <Nav tabs  className="tabs-margin employee-tab col-md-8 col-12">
            {["Personal", "Family", "Others", "Documents"].map((tab, index) => (
              <NavItem key={index}>
                <NavLink
                  style={{ cursor: "pointer" }}
                  className={classnames({
                    active: customIconActiveTab === tab,
                  })}
                  onClick={() => toggleIconCustom(tab)}
                >
                  {tab === "Personal" && (
                    <>
                      <span className="d-none d-sm-block">
                        <i className="fas fa-user"></i> Personal
                      </span>
                      <span className="d-block d-sm-none">
                        <i className="fas fa-user"></i>
                      </span>
                    </>
                  )}
                  {tab === "Family" && (
                    <>
                      <span className="d-none d-sm-block">
                        <i className="fas fa-users"></i> Family
                      </span>
                      <span className="d-block d-sm-none">
                        <i className="fas fa-users"></i>
                      </span>
                    </>
                  )}
                  {tab === "Others" && (
                    <>
                      <span className="d-none d-sm-block">
                        <i className="fas fa-ellipsis-h"></i> Others
                      </span>
                      <span className="d-block d-sm-none">
                        <i className="fas fa-ellipsis-h"></i>
                      </span>
                    </>
                  )}
                  {tab === "Documents" && (
                    <>
                      <span className="d-none d-sm-block">
                        <i className="fas fa-file-alt"></i> Documents
                      </span>
                      <span className="d-block d-sm-none">
                        <i className="fas fa-file-alt"></i>
                      </span>
                    </>
                  )}
                </NavLink>
              </NavItem>
            ))}
          </Nav>
          <div className="d-flex pb-2 col-md-4 col-12 create-btn">
            <button
              type="button"
              className="btn btn-success p-2 px-4 rounded shadow-sm"
              onClick={() => handleSubmit()}
              style={{ transition: "all 0.3s ease" }}
            >
              <i className="fas fa-check-circle mr-2"></i> Submit
            </button>
            <button
              className="btn btn-secondary p-2 px-4 rounded shadow-sm ms-2"
              onClick={() => handleCancel()}
              style={{ transition: "all 0.3s ease" }}
            >
              <i className="fas fa-times-circle mr-2"></i> Cancel
            </button>
          </div>
        </div>

        <TabContent activeTab={customIconActiveTab} className="p-3 text-muted">
          {["Personal", "Family", "Others", "Documents"].map((tab) => (
            <TabPane key={tab} tabId={tab}>
              <Row>
                <Col sm="12">
                  {tab === "Personal" ? (
                    <EmployeePersonal onChange={setPersonalData} formikRef={formikRefs.personal} dropdownData={dropdownData}/>
                  ) : tab === "Family" ? (
                    <EmployeeFamily onChange={setFamilyData}  formikRef={formikRefs.family} dropdownData={dropdownData}/>
                  ) : tab === "Others" ? (
                    <EmployeeOthers onChange={setOthersData} />
                  ) : tab === "Documents" ? (
                    <EmployeeDocuments onChange={handleDocumentDataChange} formikRef={formikRefs.documents} />
                  ) : (
                    <CardText className="mb-0">{`Content for ${tab} Tab`}</CardText>
                  )}
                </Col>
              </Row>
            </TabPane>
          ))}
        </TabContent>
      </CardBody>
    </Card>
  </Col>
</Row> */}






        </Container>
      </div>
    </React.Fragment >


  );
};

export default EmployeeCreation;
